import React, { useEffect, useMemo, useState } from 'react';
import { AppShell, PageContainer } from '../../features/ui-system';
import { CreditCard, Home, LogOut, Map, MessageSquare, Network, Newspaper, Pencil, RefreshCw, Search, Settings, Trash2, UserCog, Users, Wrench, X } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import StaffPageHeader from '../../components/StaffPageHeader';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import ktcLogo from '../../assets/ktc.png';
import { resolveBackendBaseUrl } from '../../utils/runtimeConfig';
import StaffPageLoadingOverlay from '../../components/StaffPageLoadingOverlay';

const API = `${resolveBackendBaseUrl()}/api`;

const sanitizeRole = (role) => {
  const value = String(role || '').trim().toLowerCase();
  if (value === 'superadmin' || value === 'administrator' || value === 'admin') return 'superadmin';
  if (value === 'noc') return 'noc';
  if (value === 'technician') return 'technician';
  if (value === 'investor') return 'investor';
  return 'superadmin';
};

const ROLE_LABEL = {
  superadmin: 'Superadmin',
  noc: 'NOC',
  technician: 'Teknisi',
  investor: 'Investor',
};

const toApiRole = (role) => {
  const normalized = sanitizeRole(role);
  if (normalized === 'superadmin') return 'admin';
  return normalized;
};

const buildEditForm = (user) => ({
  id: user?.id || '',
  name: String(user?.name || ''),
  username: String(user?.username || ''),
  role: sanitizeRole(user?.role),
  password: '',
  confirmPassword: '',
});

const AdminUsers = () => {
  const [sessionUser, setSessionUser] = useState(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [userPage, setUserPage] = useState(1);
  const [userPerPage, setUserPerPage] = useState(20);

  const [editingUser, setEditingUser] = useState(null);
  const [editForm, setEditForm] = useState(buildEditForm(null));
  const [saving, setSaving] = useState(false);

  const [deletingUser, setDeletingUser] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [forcingLogoutUserId, setForcingLogoutUserId] = useState('');
  const [createForm, setCreateForm] = useState({
    name: '',
    username: '',
    role: 'technician',
    password: '',
  });
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    const storedUser = localStorage.getItem('ktc_user');
    if (storedUser) {
      try {
        setSessionUser(JSON.parse(storedUser));
      } catch {
        setSessionUser(null);
      }
    }
  }, []);

  const getHeaders = () => {
    const token = localStorage.getItem('ktc_token');
    return { Authorization: `Bearer ${token}` };
  };

  const fetchUsers = async (showLoader = true) => {
    if (showLoader) setLoading(true);
    try {
      const response = await axios.get(`${API}/users`, { headers: getHeaders() });
      setUsers(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal memuat data user');
      setUsers([]);
    } finally {
      if (showLoader) setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers(true);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const filteredUsers = useMemo(() => {
    const keyword = String(query || '').trim().toLowerCase();
    return users.filter((item) => {
      const role = sanitizeRole(item.role);
      if (roleFilter !== 'all' && role !== roleFilter) return false;
      if (!keyword) return true;
      const haystack = [item.name, item.username, ROLE_LABEL[role], role]
        .map((v) => String(v || '').toLowerCase())
        .join(' ');
      return haystack.includes(keyword);
    });
  }, [users, query, roleFilter]);

  const totalFilteredUsers = filteredUsers.length;
  const totalUserPages = Math.max(1, Math.ceil(totalFilteredUsers / userPerPage));
  const pagedUsers = useMemo(() => {
    const start = (userPage - 1) * userPerPage;
    return filteredUsers.slice(start, start + userPerPage);
  }, [filteredUsers, userPage, userPerPage]);

  useEffect(() => {
    setUserPage(1);
  }, [query, roleFilter, userPerPage]);

  useEffect(() => {
    if (userPage > totalUserPages) {
      setUserPage(totalUserPages);
    }
  }, [userPage, totalUserPages]);

  const openEditModal = (user) => {
    setEditingUser(user);
    setEditForm(buildEditForm(user));
  };

  const closeEditModal = () => {
    setEditingUser(null);
    setEditForm(buildEditForm(null));
    setSaving(false);
  };

  const handleSaveEdit = async () => {
    if (!editingUser?.id) return;
    const payload = {
      name: String(editForm.name || '').trim(),
      username: String(editForm.username || '').trim(),
      role: sanitizeRole(editForm.role),
    };

    const newPassword = String(editForm.password || '').trim();
    const confirmPassword = String(editForm.confirmPassword || '').trim();
    if (newPassword || confirmPassword) {
      if (newPassword.length < 6) {
        toast.error('Password minimal 6 karakter');
        return;
      }
      if (newPassword !== confirmPassword) {
        toast.error('Konfirmasi password tidak sama');
        return;
      }
      payload.password = newPassword;
    }

    if (!payload.name || !payload.username) {
      toast.error('Nama dan username wajib diisi');
      return;
    }

    setSaving(true);
    try {
      await axios.put(`${API}/users/${editingUser.id}`, payload, { headers: getHeaders() });
      toast.success(`User ${payload.username} berhasil diperbarui`);
      closeEditModal();
      fetchUsers(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal memperbarui user');
      setSaving(false);
    }
  };

  const handleDeleteUser = async () => {
    if (!deletingUser?.id) return;
    setDeleting(true);
    try {
      await axios.delete(`${API}/users/${deletingUser.id}`, { headers: getHeaders() });
      toast.success(`User ${deletingUser.username || deletingUser.name} berhasil dihapus`);
      setDeletingUser(null);
      fetchUsers(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal menghapus user');
      setDeleting(false);
    }
  };

  const handleForceLogoutUser = async (user) => {
    const userId = String(user?.id || '').trim();
    if (!userId) return;
    if (!window.confirm(`Paksa logout user ${user?.username || user?.name || '-'} sekarang?`)) return;

    setForcingLogoutUserId(userId);
    try {
      const response = await axios.post(`${API}/auth/force-logout/${userId}`, {}, { headers: getHeaders() });
      const sessionsRevoked = Number(response.data?.sessions_revoked || 0);
      toast.success(`Force logout selesai. Session dihapus: ${sessionsRevoked}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal force logout user');
    } finally {
      setForcingLogoutUserId('');
    }
  };

  const handleCreateUser = async (event) => {
    event.preventDefault();
    const name = String(createForm.name || '').trim();
    const username = String(createForm.username || '').trim();
    const password = String(createForm.password || '').trim();
    const role = toApiRole(createForm.role);

    if (!name || !username || !password) {
      toast.error('Nama, username, dan password wajib diisi');
      return;
    }
    if (password.length < 6) {
      toast.error('Password minimal 6 karakter');
      return;
    }

    setCreating(true);
    try {
      await axios.post(`${API}/auth/register`, { name, username, password, role }, { headers: getHeaders() });
      toast.success(`User ${username} berhasil ditambahkan`);
      setCreateForm({ name: '', username: '', role: 'technician', password: '' });
      fetchUsers(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal menambah user');
    } finally {
      setCreating(false);
    }
  };

  return (
    <>
      {loading && (
        <StaffPageLoadingOverlay text="Memuat data user..." />
      )}

      <AppShell>
      <PageContainer>
        <StaffPageHeader
          eyebrow="Admin"
          title="Manajemen User"
          subtitle="Tambah user di atas, lalu kelola melalui tabel di bawah."
          actions={(
            <Button onClick={() => fetchUsers(true)} variant="outline" className="btn-secondary" disabled={loading}>
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />Muat Ulang
            </Button>
          )}
        />

        <div className="ktc-list-card rounded-xl border p-4 md:p-5">
          <div className="mb-3">
            <p className="text-base font-semibold text-white">Form Tambah User</p>
            <p className="text-xs text-gray-400">Gunakan form ini untuk membuat akun teknisi, NOC, investor, atau superadmin.</p>
          </div>
          <form onSubmit={handleCreateUser} className="grid gap-3 md:grid-cols-4">
            <Input
              value={createForm.name}
              onChange={(event) => setCreateForm((prev) => ({ ...prev, name: event.target.value }))}
              placeholder="Nama lengkap"
              className="bg-[#0A0A0A] border-white/10 text-white placeholder:text-gray-500"
              required
            />
            <Input
              value={createForm.username}
              onChange={(event) => setCreateForm((prev) => ({ ...prev, username: event.target.value }))}
              placeholder="Username login"
              className="bg-[#0A0A0A] border-white/10 text-white placeholder:text-gray-500"
              required
            />
            <Select value={sanitizeRole(createForm.role)} onValueChange={(value) => setCreateForm((prev) => ({ ...prev, role: value }))}>
              <SelectTrigger className="bg-[#0A0A0A] border-white/10 text-white">
                <SelectValue placeholder="Role user" />
              </SelectTrigger>
              <SelectContent className="bg-[#121212] border-white/10">
                <SelectItem value="superadmin" className="text-white">Superadmin</SelectItem>
                <SelectItem value="noc" className="text-white">NOC</SelectItem>
                <SelectItem value="technician" className="text-white">Teknisi</SelectItem>
                <SelectItem value="investor" className="text-white">Investor</SelectItem>
              </SelectContent>
            </Select>
            <Input
              type="password"
              value={createForm.password}
              onChange={(event) => setCreateForm((prev) => ({ ...prev, password: event.target.value }))}
              placeholder="Password (min 6)"
              className="bg-[#0A0A0A] border-white/10 text-white placeholder:text-gray-500"
              required
            />
            <div className="md:col-span-4">
              <Button type="submit" className="btn-primary" disabled={creating}>
                {creating ? 'Menyimpan...' : 'Tambah User'}
              </Button>
            </div>
          </form>
        </div>

        <div className="ktc-filter-bar grid md:grid-cols-4 gap-3 rounded-xl border p-3">
          <div className="md:col-span-2">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
              <Input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Cari nama atau username" className="pl-10 bg-[#0A0A0A] border-white/10 text-white placeholder:text-gray-500" />
            </div>
          </div>
          <div>
            <Select value={roleFilter} onValueChange={setRoleFilter}>
              <SelectTrigger className="bg-[#0A0A0A] border-white/10 text-white"><SelectValue placeholder="Filter role" /></SelectTrigger>
              <SelectContent className="bg-[#121212] border-white/10">
                <SelectItem value="all" className="text-white">Semua Role</SelectItem>
                <SelectItem value="superadmin" className="text-white">Superadmin</SelectItem>
                <SelectItem value="noc" className="text-white">NOC</SelectItem>
                <SelectItem value="technician" className="text-white">Teknisi</SelectItem>
                <SelectItem value="investor" className="text-white">Investor</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Button
              type="button"
              variant="outline"
              className="w-full h-10 btn-secondary"
              onClick={() => {
                setQuery('');
                setRoleFilter('all');
                setUserPage(1);
              }}
            >
              Reset Filter
            </Button>
          </div>
        </div>

        <div className="ktc-table-shell rounded-xl border overflow-hidden">
          {!loading && filteredUsers.length > 0 ? (
            <div className="ktc-pagination-bar flex flex-col gap-3 border-b px-4 py-3 md:flex-row md:items-center md:justify-between">
              <p className="text-xs">
                Menampilkan {(userPage - 1) * userPerPage + 1}-{Math.min(userPage * userPerPage, totalFilteredUsers)} dari {totalFilteredUsers} user
              </p>
              <div className="flex flex-wrap items-center gap-2">
                <Select value={String(userPerPage)} onValueChange={(v) => setUserPerPage(Number(v))}>
                  <SelectTrigger className="h-8 w-[130px] bg-[#0A0A0A] border-white/10 text-xs text-white">
                    <SelectValue placeholder="Baris/halaman" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#121212] border-white/10">
                    <SelectItem value="10" className="text-white">10 / halaman</SelectItem>
                    <SelectItem value="20" className="text-white">20 / halaman</SelectItem>
                    <SelectItem value="50" className="text-white">50 / halaman</SelectItem>
                    <SelectItem value="100" className="text-white">100 / halaman</SelectItem>
                  </SelectContent>
                </Select>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  className="btn-secondary h-8 px-3 text-xs"
                  disabled={userPage <= 1}
                  onClick={() => setUserPage(1)}
                >
                  First
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  className="btn-secondary h-8 px-3 text-xs"
                  disabled={userPage <= 1}
                  onClick={() => setUserPage((p) => Math.max(1, p - 1))}
                >
                  Prev
                </Button>
                <span className="text-xs">Hal {userPage}/{totalUserPages}</span>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  className="btn-secondary h-8 px-3 text-xs"
                  disabled={userPage >= totalUserPages}
                  onClick={() => setUserPage((p) => Math.min(totalUserPages, p + 1))}
                >
                  Next
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  className="btn-secondary h-8 px-3 text-xs"
                  disabled={userPage >= totalUserPages}
                  onClick={() => setUserPage(totalUserPages)}
                >
                  Last
                </Button>
              </div>
            </div>
          ) : null}
          <div className="overflow-x-auto">
            <table className="ktc-data-table w-full min-w-[760px] text-sm">
              <thead className="text-gray-300">
                <tr>
                  <th className="sticky top-0 z-10 text-left px-4 py-3 font-medium">Nama</th>
                  <th className="sticky top-0 z-10 text-left px-4 py-3 font-medium">Username</th>
                  <th className="sticky top-0 z-10 text-left px-4 py-3 font-medium">Role</th>
                  <th className="sticky top-0 z-10 text-left px-4 py-3 font-medium">Dibuat</th>
                  <th className="sticky top-0 z-10 text-right px-4 py-3 font-medium">Aksi</th>
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr><td colSpan={5} className="px-4 py-6 text-gray-400">Memuat data user...</td></tr>
                )}
                {!loading && filteredUsers.length === 0 && (
                  <tr><td colSpan={5} className="px-4 py-6 text-gray-500">Tidak ada user yang cocok.</td></tr>
                )}
                {!loading && pagedUsers.map((item) => {
                  const role = sanitizeRole(item.role);
                  const isSelf = String(item.id || '') === String(sessionUser?.id || '');
                  return (
                    <tr key={item.id} className="border-t border-white/10 text-gray-200">
                      <td className="px-4 py-3">{item.name || '-'}</td>
                      <td className="px-4 py-3">{item.username || '-'}</td>
                      <td className="px-4 py-3">{ROLE_LABEL[role] || role}</td>
                      <td className="px-4 py-3 text-gray-400">{item.created_at ? new Date(item.created_at).toLocaleString('id-ID') : '-'}</td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-end gap-2">
                          <Button size="sm" variant="outline" className="border-white/20 text-white hover:bg-white/10" onClick={() => openEditModal(item)}>
                            <Pencil className="w-4 h-4 mr-1" />Edit
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className="border-amber-500/40 text-amber-300 hover:bg-amber-500/10"
                            disabled={forcingLogoutUserId === String(item.id || '')}
                            onClick={() => handleForceLogoutUser(item)}
                            title="Paksa logout user (hapus semua session aktif)"
                          >
                            <LogOut className={`w-4 h-4 mr-1 ${forcingLogoutUserId === String(item.id || '') ? 'animate-pulse' : ''}`} />
                            Logout
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className="border-red-500/40 text-red-300 hover:bg-red-500/10"
                            disabled={isSelf}
                            onClick={() => setDeletingUser(item)}
                            title={isSelf ? 'Akun sendiri tidak bisa dihapus' : 'Hapus user'}
                          >
                            <Trash2 className="w-4 h-4 mr-1" />Hapus
                          </Button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
                  </PageContainer>
    </AppShell>

      <Dialog open={Boolean(editingUser)} onOpenChange={(open) => { if (!open) closeEditModal(); }}>
        <DialogContent className="border-white/10 bg-[#0A0A0A] text-white">
          <DialogHeader>
            <DialogTitle>Edit User</DialogTitle>
            <DialogDescription className="text-gray-400">Ubah data user lalu simpan.</DialogDescription>
          </DialogHeader>

          <div className="space-y-3">
            <div>
              <label className="staff-form-label-xs">Nama</label>
              <Input value={editForm.name} onChange={(event) => setEditForm((prev) => ({ ...prev, name: event.target.value }))} className="bg-[#121212] border-white/10 text-white" />
            </div>
            <div>
              <label className="staff-form-label-xs">Username</label>
              <Input value={editForm.username} onChange={(event) => setEditForm((prev) => ({ ...prev, username: event.target.value }))} className="bg-[#121212] border-white/10 text-white" />
            </div>
            <div>
              <label className="staff-form-label-xs">Role</label>
              <Select value={sanitizeRole(editForm.role)} onValueChange={(value) => setEditForm((prev) => ({ ...prev, role: value }))}>
                <SelectTrigger className="bg-[#121212] border-white/10 text-white"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#121212] border-white/10">
                  <SelectItem value="superadmin" className="text-white">Superadmin</SelectItem>
                  <SelectItem value="noc" className="text-white">NOC</SelectItem>
                  <SelectItem value="technician" className="text-white">Teknisi</SelectItem>
                  <SelectItem value="investor" className="text-white">Investor</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="staff-form-label-xs">Password Baru (opsional)</label>
              <Input
                type="password"
                value={editForm.password}
                onChange={(event) => setEditForm((prev) => ({ ...prev, password: event.target.value }))}
                className="bg-[#121212] border-white/10 text-white"
                placeholder="Kosongkan jika tidak diubah"
              />
            </div>
            <div>
              <label className="staff-form-label-xs">Konfirmasi Password Baru</label>
              <Input
                type="password"
                value={editForm.confirmPassword}
                onChange={(event) => setEditForm((prev) => ({ ...prev, confirmPassword: event.target.value }))}
                className="bg-[#121212] border-white/10 text-white"
                placeholder="Ulangi password baru"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" className="btn-secondary" onClick={closeEditModal} disabled={saving}>
              <X className="w-4 h-4 mr-1" />Batal
            </Button>
            <Button className="btn-primary" onClick={handleSaveEdit} disabled={saving}>
              {saving ? 'Menyimpan...' : 'Simpan Perubahan'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={Boolean(deletingUser)} onOpenChange={(open) => { if (!open) { setDeletingUser(null); setDeleting(false); } }}>
        <DialogContent className="border-white/10 bg-[#0A0A0A] text-white">
          <DialogHeader>
            <DialogTitle>Hapus User</DialogTitle>
            <DialogDescription className="text-gray-400">
              Yakin ingin menghapus user <span className="text-white font-medium">{deletingUser?.username || deletingUser?.name}</span>?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" className="btn-secondary" onClick={() => { setDeletingUser(null); setDeleting(false); }} disabled={deleting}>
              Batal
            </Button>
            <Button variant="outline" className="border-red-500/40 text-red-300 hover:bg-red-500/10" onClick={handleDeleteUser} disabled={deleting}>
              {deleting ? 'Menghapus...' : 'Hapus'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default AdminUsers;













